import React, { ChangeEvent } from 'react'

import { CheckboxInput, Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'

import Button from 'components/Button'
import { CTAContainer } from 'components/CTAContainer'
import {
  FormItem,
  FormRow,
  StyledFormContainer,
  StyledTextInput,
} from 'components/FormContainer/styled-components'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { PageName } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import { shouldGrayOutCTACall } from 'utils/shouldGrayOutCTA'

import { validate } from './form'
import { Center, StyledHeroHeader } from './styled-components'
import { PersonalInfoProps } from './types'

// Create a trackable Submit Personal Info button
export const SubmitPersonalInfo = withClickTrack<any, any>(TRACKER_IDS.PERSONAL_INFO.NEXT_BUTTON)(
  Button,
)

const PersonalInfo: React.FC<PersonalInfoProps> = ({
  errors,
  footnote,
  footnoteLock,
  submitCta,
  isSubmitting,
  isValid,
  onChange,
  submitForm,
  values,
  loading,
  isDynamicFunnelEnabled,
  isBackEnabled,
}) => {
  const { getRemoteString } = useRemoteStrings()
  const handleDobMask = (e: ChangeEvent<HTMLInputElement>) => {
    const element = e
    const x = element.target.value.replace(/\D/g, '').match(/(\d{0,2})(\d{0,2})(\d{0,4})/)
    element.target.value = !x[2] ? x[1] : x[1] + '/' + x[2] + (x[3] ? '/' + x[3] : '')
    return onChange(element)
  }

  const shouldGrayOutCTA = shouldGrayOutCTACall({
    values: values,
    validation: validate,
    ignoredKeys: ['usCitizen'],
  })

  return (
    <>
      <Nav
        back={isDynamicFunnelEnabled && isBackEnabled(PageName.PersonalInfo)}
        backClickTracker={TRACKER_IDS.PERSONAL_INFO.BACK_BUTTON}
      />
      <Layout footnoteLock={footnoteLock} footnote={footnote}>
        <StyledHeroHeader>
          {getRemoteString({ key: 'registration.personal_information.subtitle' })}
        </StyledHeroHeader>
        <StyledFormContainer>
          {loading ? (
            <Center>
              <Spinner />
            </Center>
          ) : (
            <>
              <FormRow>
                <FormItem>
                  <StyledTextInput
                    label={{
                      value: 'Legal First Name',
                    }}
                    className="text-input"
                    inputProps={{
                      name: 'firstName',
                      value: values.firstName,
                      onChange,
                      id: 'firstName',
                      type: 'text',
                      placeholder: 'John',
                    }}
                    error={!!errors.firstName}
                    errorText={errors.firstName as string}
                  />
                </FormItem>
                <FormItem>
                  <StyledTextInput
                    label={{
                      value: 'Legal Last Name',
                    }}
                    className="text-input"
                    inputProps={{
                      name: 'lastName',
                      value: values.lastName,
                      onChange,
                      id: 'lastName',
                      type: 'text',
                      placeholder: 'Doe',
                    }}
                    error={!!errors.lastName}
                    errorText={errors.lastName as string}
                  />
                </FormItem>
                <FormItem>
                  <StyledTextInput
                    label={{
                      value: 'Birthday',
                    }}
                    className="text-input"
                    inputProps={{
                      name: 'dob',
                      value: values.dob,
                      onChange: handleDobMask,
                      id: 'dob',
                      type: 'text',
                      placeholder: 'MM/DD/YYYY',
                      inputMode: 'numeric',
                    }}
                    error={!!errors.dob}
                    errorText={errors.dob as string}
                  />
                </FormItem>
                <FormItem toTheLeft>
                  <CheckboxInput
                    className="us-citizen-checkbox"
                    checked={values.usCitizen}
                    name="usCitizen"
                    id="usCitizen"
                    label={getRemoteString({
                      key: 'registration.personal_information.us_citizen_input.title',
                    })}
                    onChange={onChange}
                  />
                </FormItem>
              </FormRow>
            </>
          )}
        </StyledFormContainer>
        <Separator />
        <CTAContainer>
          <SubmitPersonalInfo
            styleType={!isValid || shouldGrayOutCTA ? 'invalid' : undefined}
            onPress={submitForm}
            data-testid={TestIDs.PERSONAL_INFO.SUBMIT_CTA}
            disabled={isSubmitting || loading}
            buttonType={'submit'}
          >
            {isSubmitting ? <Spinner height={20} width={20} white={true} /> : submitCta}
          </SubmitPersonalInfo>
        </CTAContainer>
      </Layout>
    </>
  )
}

export default PersonalInfo
